import React, { useState, useCallback, useRef } from 'react';
import Papa from 'papaparse';

export interface DataInputProps {
  onDataParsed: (data: string[][], headers?: string[]) => void;
  placeholder?: string;
  label?: string;
  acceptedFileTypes?: string;
  className?: string;
}

export const DataInput: React.FC<DataInputProps> = ({
  onDataParsed,
  placeholder = 'Enter data, one item per line\nFor multiple columns, use comma separation (CSV format)',
  label = 'Data Input',
  acceptedFileTypes = '.csv',
  className = '',
}) => {
  const [mode, setMode] = useState<'text' | 'file'>('text');
  const modes = [
    { value: 'text' as const, label: 'Text Input' },
    { value: 'file' as const, label: 'File Upload' },
  ];
  const [textValue, setTextValue] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [itemCount, setItemCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseContent = useCallback(
    async (content: string): Promise<string[][]> => {
      return new Promise((resolve, reject) => {
        Papa.parse(content, {
          header: false,
          dynamicTyping: false,
          skipEmptyLines: true,
          complete: (results) => {
            const data = results.data as string[][];
            const filteredData = data.filter((row) =>
              row.some((cell) => cell && cell.trim() !== ''),
            );
            resolve(filteredData);
          },
          error: (error: Error) => {
            reject(new Error(`CSV parsing error: ${error.message}`));
          },
        });
      });
    },
    [],
  );

  const handleTextChange = useCallback(
    async (value: string) => {
      setTextValue(value);
      setError(null);

      if (!value.trim()) {
        setItemCount(0);
        onDataParsed([]);
        return;
      }

      try {
        const parsedData = await parseContent(value);
        setItemCount(parsedData.length);
        onDataParsed(parsedData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error parsing data');
        setItemCount(0);
        onDataParsed([]);
      }
    },
    [parseContent, onDataParsed],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.name.endsWith('.csv')) {
        setError('Please upload a CSV file');
        return;
      }

      setSelectedFile(file);
      setError(null);

      Papa.parse(file, {
        header: false,
        delimiter: ',',
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            setError('Error parsing CSV file');
            setItemCount(0);
            onDataParsed([]);
            return;
          }
          const data = results.data as string[][];
          const headers = data[0] || [];
          const dataWithoutHeader = data.slice(1);
          setItemCount(dataWithoutHeader.length);
          onDataParsed(dataWithoutHeader, headers);
        },
        error: (error: Error) => {
          setError(`Error reading file: ${error.message}`);
          setItemCount(0);
          onDataParsed([]);
        },
      });
    },
    [onDataParsed],
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setItemCount(0);
    setError(null);
    onDataParsed([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onDataParsed]);

  const handleModeChange = useCallback(
    (newMode: 'text' | 'file') => {
      setMode(newMode);
      setError(null);
      setItemCount(0);
      onDataParsed([]);

      if (newMode === 'text') {
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setTextValue('');
      }
    },
    [onDataParsed],
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {label && (
        <label className="block text-sm text-broccoli-neutral-700">
          {label}
        </label>
      )}

      <div className="inline-flex rounded-lg border border-broccoli-neutral-300 p-1 bg-broccoli-neutral-100">
        {modes.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => handleModeChange(value)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              mode === value
                ? 'bg-white text-broccoli-primary-600 shadow-sm border border-broccoli-primary-200'
                : 'text-broccoli-neutral-600 hover:text-broccoli-neutral-800 hover:bg-white/50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'text' ? (
        <div className="space-y-2">
          <textarea
            value={textValue}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={placeholder}
            className="w-full min-h-[200px] p-4 rounded-md border border-broccoli-neutral-300 shadow-sm font-mono text-sm focus:ring-2 focus:ring-broccoli-primary-500 focus:border-broccoli-primary-500"
          />
        </div>
      ) : (
        <div className="border-2 border-dashed border-broccoli-neutral-300 rounded-lg p-6 text-center transition-colors hover:border-broccoli-neutral-400">
          {!selectedFile ? (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFileTypes}
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-broccoli-primary-600 hover:text-broccoli-primary-700 font-medium"
              >
                Select a CSV File
              </label>
              <p className="mt-1 text-sm text-broccoli-neutral-500">
                Click to upload
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-broccoli-neutral-600">
                  {selectedFile.name}
                </span>
                <button
                  onClick={handleRemoveFile}
                  className="text-sm text-broccoli-neutral-600 hover:text-broccoli-error px-2 py-1 rounded hover:bg-broccoli-neutral-100"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <div>
          {itemCount > 0 && (
            <span className="text-broccoli-neutral-600">
              {itemCount} {itemCount === 1 ? 'row' : 'rows'} detected
            </span>
          )}
        </div>
        {error && <span className="text-broccoli-error">{error}</span>}
      </div>
    </div>
  );
};
